// Routes a frame to the active detector — QR (zxing-wasm, today's path)
// or one of the four fiducial formats (opencv.js ArucoDetector). Exactly
// one detector runs per frame; side-by-side detection is explicitly out
// (see docs/plans/2026-06-29-001-feat-fiducial-active-format-picker-plan.md
// for the side-by-side → single-format pivot rationale).

import type { ActiveFormat } from '../types';
import { decodeFiducials, isFiducialFormat } from './fiducialDecoder';
import { decodeFrame, type DecodeOutcome } from './qrDecoder';
import { decodeText, type DecodeTextOptions } from './decodeText';

// Dev-only per-frame perf log. Prod builds skip the JSON.stringify + the
// devtools protocol round-trip entirely — measurable at 10-30 Hz.
const PERF_LOG_ENABLED = import.meta.env.DEV;

export interface DecodeActiveFormatOptions {
  /** Passed through to `decodeText` when activeFormat === 'text'. Ignored otherwise. */
  text?: DecodeTextOptions;
}

export async function decodeActiveFormat(
  imageData: ImageData,
  activeFormat: ActiveFormat,
  opts?: DecodeActiveFormatOptions
): Promise<DecodeOutcome> {
  // Phase 5.1: temporary perf instrumentation for the fiducial range test.
  // Grep [fiducial-perf] out of the recorded devtools log to compute p50/p95
  // per format. Strip in Phase 5.4.
  const startTs = performance.now();
  let outcome: DecodeOutcome;
  if (activeFormat === 'text') {
    outcome = await decodeText(imageData, opts?.text);
  } else if (isFiducialFormat(activeFormat)) {
    outcome = await decodeFiducials(imageData, activeFormat);
  } else {
    outcome = await decodeFrame(imageData);
  }
  const endTs = performance.now();
  if (PERF_LOG_ENABLED) {
    const hit = outcome.kind === 'symbols' ? outcome.symbols.length : 0;
    console.debug(
      '[fiducial-perf]',
      JSON.stringify({
        activeFormat,
        decodeMs: +(endTs - startTs).toFixed(2),
        hit,
        w: imageData.width,
        h: imageData.height,
      })
    );
  }
  return outcome;
}
